import React from 'react';

import { PageHeroProps } from '../../_blocks/PageHeroGraphQL/types';
import classes from './PageHero.module.scss';
import { DebugJsonBlock } from '../DebugJsonBlock';
import type { Media as MediaType } from '../../../payload/payload-types';
import { Media } from '../Media';
import { Button } from '../Button';
import ResimdoIcon from '../ResimdoIcon';
import { Gutter } from '../Gutter';
import { SemanticHeading } from '../SemanticHeading';
import { renderTrademarkText } from '../TrademarkText';

const renderPageHeroTitle = (text: string): React.ReactNode => {
  const segments = text.split(/\n+/);
  return (
    <>
      {segments.map((segment, i) => (
        <React.Fragment key={i}>
          {i > 0 && <br />}
          {renderTrademarkText(segment)}
        </React.Fragment>
      ))}
    </>
  );
};

export const PageHero: React.FC<{
  className?: string;
  pageHero: PageHeroProps;
}> = ({ className, pageHero }) => {
  const image = pageHero.heroImage?.media as unknown as MediaType;
  const { buttons, icons } = pageHero;

  return (
    <Gutter>
      <div className={[className, classes.wrapper, 'grid'].filter(Boolean).join(' ')}>
        {image && (
          <div className={`col-12 ${classes.mobileImage}`}>
            <Media
              resource={image}
              alt={image.alt || ''}
              className={classes.image}
              imageSize="widescreen"
              priority={true}
            />
          </div>
        )}
        <div
          className={`col-6 col-sm-12 ${classes.leftContent} ${image ? classes.topPadding : ''}`}
        >
          <SemanticHeading
            heading={pageHero.semanticTitle}
            fallbackText={pageHero.title_legacy}
            fallbackTag="h1"
            className={`heading-h1 ${classes.heading}`}
          >
            {(pageHero.semanticTitle?.text || pageHero.title_legacy) && (
              renderPageHeroTitle(pageHero.semanticTitle?.text || pageHero.title_legacy)
            )}
          </SemanticHeading>
          <p>{pageHero.description ? renderTrademarkText(pageHero.description) : null}</p>
          <div className={classes.actionRow}>
            {buttons.map(({ button }) =>
              button.hideButton ? null : (
                <Button
                  key={button.label}
                  type={button.type || 'reference'}
                  appearance={button.style}
                  href={button.type === 'custom' ? button?.url : undefined}
                  reference={button.type === 'reference' ? button?.reference : undefined}
                  document={
                    button.type === 'download' && button.document && typeof button.document === 'object'
                      ? button.document
                      : undefined
                  }
                  label={button.label}
                  newTab={button.newTab}
                  width="200px"
                />
              ),
            )}
            <div className={classes.icons}>
              {icons.map(({ id, icon }, index) => {
                return <ResimdoIcon key={`${id}-${index}`} icon={icon} size="SM" />;
              })}
            </div>
          </div>
        </div>
        {image && (
          <div className={`col-6 ${classes.desktopImage}`}>
            <Media
              resource={image}
              alt={image.alt || ''}
              className={classes.image}
              imageSize="widescreen"
              priority={true}
            />
          </div>
        )}
        <DebugJsonBlock block={{ ...pageHero }} />
      </div>
    </Gutter>
  );
};

export default PageHero;
